import { PageResponseNoticeDto } from "apis/data-contracts";
import { PlusIcon } from "assets/icons";
import Board from "components/Common/Board/Board";
import SearchEmpty from "components/Common/Empty/SearchEmpty";
import { PageFilterPanel } from "components/Common/PageFilterPannel";
import FilterSearch from "components/Common/PageFilterPannel/FilterSearch";
import { Table } from "components/Common/Table/Table";
import Button from "components/foundation/Button";
import Pagination from "components/foundation/Pagination";
import React, { useState } from "react";
import styled from "styled-components";
import { match } from "ts-pattern";
import NoticeDashboardTableRow from "./NoticeDashboardTableRow";
import { useFormContext } from "react-hook-form";
import { NoticeDashboardFormData } from "..";
import useNoticesAutoCompleteQuery from "features/notice/hooks/queries/useNoticesAutoCompleteQuery";
import { Link } from "react-router-dom";
import { ROUTER_PATH } from "config/path";
import DateRangePicker from "components/foundation/DateRangePicker";
import FilterListItem from "components/Common/PageFilterPannel/FilterListItem";

interface NoticeDashboardWebPageProps {
  data: PageResponseNoticeDto;
  page: number;
  setSearchOptions: React.Dispatch<
    React.SetStateAction<{
      searchType: string;
      search: string;
      startDate: string | undefined;
      endDate: string | undefined;
      page: number;
    }>
  >;
}

const NoticeDashboardWebPage = ({
  data,
  page,
  setSearchOptions,
}: NoticeDashboardWebPageProps) => {
  const { watch, setValue } = useFormContext<NoticeDashboardFormData>();

  const [type, keyword] = [watch("searchType"), watch("search")];

  const { data: autoCompleteData } = useNoticesAutoCompleteQuery({
    type,
    keyword,
  });

  const [isReset, setIsReset] = useState<boolean>();
  const handleSearchReset = (): void => {
    setSearchOptions((pre) => ({
      ...pre,
      search: "",
      searchType: "title",
      startDate: "",
      endDate: "",
      page: 1,
    }));

    setValue("search", "");
    setValue("searchType", "title");
    setIsReset(!isReset);
  };

  return (
    <NoticeDashboardWebPageLayout>
      <NoticeDashboardWebPageHeader>
        <h1>NOTICE</h1>
        <Link to={ROUTER_PATH.notice.create}>
          <Button size="large" rightIcon={<PlusIcon />}>
            공지사항 등록하기
          </Button>
        </Link>
      </NoticeDashboardWebPageHeader>

      <PageFilterPanel.Board>
        <PageFilterPanel.NestItem>
          <FilterListItem title="등록일">
            <DateRangePicker
              onChangeDate={(d) => {
                setSearchOptions((pre) => ({
                  ...pre,
                  startDate: d.start.toString(),
                  endDate: d.end?.toString(),
                }));
              }}
              onClickReset={isReset}
            />
          </FilterListItem>
          <FilterSearch
            autocomplete={autoCompleteData?.data.result.results.map((i) => {
              return {
                value: i,
                label: i,
              };
            })}
            dropdownList={[
              {
                label: "작성자",
                value: "author",
              },
              {
                label: "제목",
                value: "title",
              },
            ]}
            onSearchReset={handleSearchReset}
          />
        </PageFilterPanel.NestItem>
      </PageFilterPanel.Board>

      <ContentBoard>
        <Table.TotalCount>총 {data.totalElements}건</Table.TotalCount>

        {match(data.totalElements)
          .with(0, () => <SearchEmpty text="등록된 공지사항이 없습니다." />)
          .otherwise(() => (
            <>
              <Table.Layout>
                <Table.Table columns="auto auto auto auto auto">
                  <Table.Thead>
                    <Table.Th>No.</Table.Th>
                    <Table.Th>제목</Table.Th>
                    <Table.Th>작성자</Table.Th>
                    <Table.Th>등록일</Table.Th>
                    <Table.Th>수정일</Table.Th>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.contents.map((notice, index) => (
                      <NoticeDashboardTableRow
                        id={notice.uuid}
                        no={data.totalElements - (index + (page - 1) * 10)}
                        key={notice.uuid}
                        title={notice.title}
                        author={notice.createdUser.name}
                        createdAt={notice.createdAt}
                        updatedAt={notice.updatedAt}
                      />
                    ))}
                  </Table.Tbody>
                </Table.Table>
              </Table.Layout>
              <Pagination
                currentPage={page}
                onClickPageButton={(page) =>
                  setSearchOptions((pre) => ({ ...pre, page }))
                }
                lastPage={data.totalPages}
                style={{
                  marginLeft: "auto",
                }}
              />
            </>
          ))}
      </ContentBoard>
    </NoticeDashboardWebPageLayout>
  );
};

export default NoticeDashboardWebPage;

const NoticeDashboardWebPageHeader = styled(Board)`
  padding: 3rem 4rem;
  display: flex;
  justify-content: space-between;

  h1 {
    font-size: 28px;
    font-weight: 600;
    line-height: 36px; /* 128.571% */
    letter-spacing: -0.56px;
  }
`;

const NoticeDashboardWebPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContentBoard = styled(Board)`
  padding: 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;
